import { useState, useEffect, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const parseValue = (val) => {
  if (val) {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return {
        day: d.getDate().toString().padStart(2, '0'),
        month: (d.getMonth() + 1).toString().padStart(2, '0'),
        year: d.getFullYear().toString()
      };
    }
  }
  return { day: '', month: '', year: '' };
};

export default function DatePicker({ value, onChange, label, required = false, disabled = false }) {
  const init = parseValue(value);
  const [day, setDay] = useState(init.day);
  const [month, setMonth] = useState(init.month);
  const [year, setYear] = useState(init.year);

  // Keep latest onChange in a ref so the second effect never goes stale
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  // Sync when the parent supplies a new value (e.g. switching from Add to Edit)
  useEffect(() => {
    const { day: d, month: m, year: y } = parseValue(value);
    setDay(d);
    setMonth(m);
    setYear(y);
  }, [value]);

  // Notify parent whenever the user changes day / month / year
  useEffect(() => {
    if (day && month && year) {
      const dateStr = `${year}-${month}-${day}`;
      if (dateStr !== value) {
        onChangeRef.current(dateStr);
      }
    }
    // value intentionally omitted – we only want to fire when the user changes a picker
  }, [day, month, year]);

  const currentYear = new Date().getFullYear();
  // Generate years from 1920 to current year
  const years = Array.from({ length: currentYear - 1919 }, (_, i) => currentYear - i);
  
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };

  const days = Array.from({ length: getDaysInMonth(year, month) }, (_, i) => 
    (i + 1).toString().padStart(2, '0')
  );

  return (
    <div>
      {label && <Label className="mb-2 block">{label} {required && '*'}</Label>}
      <div className="grid grid-cols-3 gap-2">
        {/* Day Selection */}
        <div>
          <Select value={day} onValueChange={setDay} required={required} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent>
              {days.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Month Selection */}
        <div>
          <Select value={month} onValueChange={setMonth} required={required} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Year Selection */}
        <div>
          <Select value={year} onValueChange={setYear} required={required} disabled={disabled}>
            <SelectTrigger>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
